'use client';

import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { type As, type ComponentWithAs } from '@ironhack/design-system2/types';

import { InfoMedia } from '@/app/components/server/info-media';
import { LogoList } from '@/app/components/client/logo-list';
import { Stats } from '@/app/components/server/stats';
import { type DatoModuleFormat } from '@/lib/datocms';

type Props = {
  infoMediaData: Omit<InfoMedia, 'moduleFormat'>;
  statsData: Omit<Stats, 'moduleFormat'>;
  logosData: Omit<LogoList, 'moduleFormat'>;
} & BoxProps;

export const InitialContent: ComponentWithAs<As, Props> = forwardRef(
  (props: Props, ref) => {
    const {
      infoMediaData: { button: [button] = [], ...infoMedia },
      statsData: stats,
      logosData: logos,
      ...boxProps
    } = props;

    const infoMediaData = {
      ...infoMedia,
      ...(button && { button: [{ rel: 'nofollow', ...button }] }),
      headingProps: { as: 'h2' as const, textStyle: ['5xl', null, '4xl'] },
      mediaSide: 'left' as const,
      moduleFormat: [
        {
          background: 'white',
          spacingBottom: '40',
          spacingTop: '40',
        },
      ] as [DatoModuleFormat],
    } as InfoMedia;

    const statsData = {
      ...stats,
      moduleFormat: [
        {
          background: 'white',
          spacingBottom: '40',
          spacingTop: '24',
        },
      ] as [DatoModuleFormat],
    };

    const logosData = {
      ...logos,
      moduleFormat: [
        {
          background: 'white',
          spacingBottom: '64',
          spacingTop: '24',
        },
      ] as [DatoModuleFormat],
    };

    return (
      <Box as="section" ref={ref} {...boxProps}>
        <InfoMedia
          buttonWidth={['full', null, 'fit-content']}
          data={infoMediaData}
        />
        <Stats
          data={statsData}
          innerBoxProps={{
            display: ['flex', null, 'grid'],
            flexDirection: ['column', null, null],
            gridTemplateColumns: [null, null, '33% 33% 33%'],
            gridGap: [2, null, 4],
          }}
        />
        <LogoList data={logosData} />
      </Box>
    );
  }
);
